import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, X, Send, FileText, AlertTriangle, HelpCircle, ChevronDown, ChevronRight, CreditCard, Loader2, UserPlus, Search, User as UserIcon, ListOrdered, StickyNote, Calculator, CheckCircle, Settings, Ban } from 'lucide-react';

import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { InfoTooltip } from '../components/InfoTooltip';
import InvoiceLogsSection from '../components/InvoiceLogsSection';
import MarkInvoicePaidModal from '../components/MarkInvoicePaidModal';
import CustomAlertModal from '../components/CustomAlertModal';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import { User } from '../types';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';
import { formatDateOnly, toDateInputValue, toLocalYmd } from '../utils/dateOnly';
import { searchCustomers as fuzzySearchCustomers } from '../utils/searchUtils';
import { canViewFullCustomerPhone } from '../utils/roleUtils';
import { displayCustomerPhone } from '../utils/phoneFormatUtils';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED';
type ApiInvoiceStatus = 'DRAFT' | 'SENT' | 'PAID';

const toApiInvoiceStatus = (s?: InvoiceStatus): ApiInvoiceStatus | undefined => {
  if (s === 'DRAFT' || s === 'SENT' || s === 'PAID') return s;
  return undefined;
};

type InvoiceLineItem = {
  clientId: string;
  included: boolean;
  description: string;
  qty: number;
  /** Money input stored as "cents digits" (e.g. "123" => $1.23) */
  unitPriceCents: string;
  professionalName?: string;
  serviceId?: string;
  assignmentId?: string;
  durationMinutes?: number;
  fromDateTime?: string;
  toDateTime?: string;
  source?: 'booking' | 'custom';
};

const formatMoney = (n: number): string => {
  const v = Number.isFinite(n) ? n : 0;
  return v.toFixed(2);
};

const collectionTabClipLeft: React.CSSProperties = {
  clipPath: 'polygon(0 0, 100% 0, calc(100% - 1rem) 100%, 0 100%)',
};
const collectionTabClipRight: React.CSSProperties = {
  clipPath: 'polygon(1rem 0, 100% 0, 100% 100%, 0 100%)',
};

const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/** "Cents entry" behavior: store only digits, display as dollars with 2 decimals. */
const centsDigitsToAmount = (digits: string): number => {
  const only = String(digits || '').replace(/\D/g, '');
  const cents = only ? parseInt(only, 10) : 0;
  return Number.isFinite(cents) ? cents / 100 : 0;
};

const centsDigitsToMoneyString = (digits: string): string => {
  const only = String(digits || '').replace(/\D/g, '');
  if (!only) return '';
  const cents = parseInt(only, 10);
  return formatMoney((Number.isFinite(cents) ? cents : 0) / 100);
};

const moneyToCentsDigits = (amount: any): string => {
  const n = safeNum(amount);
  const cents = Math.round(Math.max(0, n) * 100);
  return cents > 0 ? String(cents) : '';
};

/** Percent entry stored as "basis points digits" (e.g. "825" => 8.25%). */
const percentDigitsToPercent = (digits: string): number => {
  const only = String(digits || '').replace(/\D/g, '');
  const bps = only ? parseInt(only, 10) : 0;
  return Number.isFinite(bps) ? bps / 100 : 0;
};

const percentDigitsToPercentString = (digits: string): string => {
  const only = String(digits || '').replace(/\D/g, '');
  if (!only) return '';
  const bps = parseInt(only, 10);
  const pct = Number.isFinite(bps) ? bps / 100 : 0;
  return pct.toFixed(2);
};

const percentToPercentDigits = (pct: any): string => {
  const n = safeNum(pct);
  const bps = Math.round(Math.max(0, n) * 100);
  return bps > 0 ? String(bps) : '';
};

const InvoiceDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { user } = useAuth();
  const { currentBusiness, isInitialized: businessInitialized } = useBusiness();

  const fromPath = useMemo(() => {
    const st: any = location.state;
    return typeof st?.from === 'string' ? st.from : '';
  }, [location.state]);

  // Clear stored create-invoice form state when arriving at details (so it doesn't leak into Create later).
  useEffect(() => {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('createInvoiceFormState_')) keysToRemove.push(key);
      }
      keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch {
      // ignore
    }
  }, []);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [missingBusiness, setMissingBusiness] = useState(false);

  const [billTo, setBillTo] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  /** When true, show Customer Name/email/phone text fields (after "New Customer" or Clear); when false and there is customer data, show only the card. */
  const [showNewCustomerFields, setShowNewCustomerFields] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<User[]>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [searchInputReadOnly, setSearchInputReadOnly] = useState(true);
  const customerSearchContainerRef = React.useRef<HTMLDivElement>(null);
  const [emailLookupUser, setEmailLookupUser] = useState<User | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  /** Display only: which collection method block to show (matches create page layout) */
  const [collectionMethodDisplay, setCollectionMethodDisplay] = useState<'send_invoice' | 'charge_now'>('send_invoice');
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(() => toLocalYmd(new Date()));
  const [dueDate, setDueDate] = useState(() => toLocalYmd(new Date()));
  const [status, setStatus] = useState<InvoiceStatus>('DRAFT');
  /** Status last loaded from server; used for read-only so we don't lock the form when user selects PAID before saving */
  const [loadedStatus, setLoadedStatus] = useState<InvoiceStatus>('DRAFT');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const [discount, setDiscount] = useState<string>('');
  /** Tax percentage stored as basis-points digits (e.g. "825" => 8.25%) */
  const [taxPercent, setTaxPercent] = useState<string>('');
  const [includeTip, setIncludeTip] = useState(true);
  /** Tip amount stored as cents digits (same as discount) */
  const [tip, setTip] = useState<string>('');
  /** Optional note regarding the tip (private only) */
  const [tipNote, setTipNote] = useState('');

  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);
  /** Deposit and due from the loaded invoice (read-only in totals) */
  const [invoiceDeposit, setInvoiceDeposit] = useState(0);
  const [invoiceDue, setInvoiceDue] = useState(0);
  /** When status is PAID, the date (ISO string) from the latest succeeded payment */
  const [paidAt, setPaidAt] = useState<string | null>(null);
  /** Invoice record created_at (fallback for logs when CREATED event is missing) */
  const [invoiceCreatedAt, setInvoiceCreatedAt] = useState<string | null>(null);
  const [invoiceAppointmentId, setInvoiceAppointmentId] = useState<string | null>(null);
  const [invoiceAppointmentScheduledAt, setInvoiceAppointmentScheduledAt] = useState<string | null>(null);
  const [logsRefreshTrigger, setLogsRefreshTrigger] = useState(0);
  const [markPaidModalOpen, setMarkPaidModalOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const actionsDropdownDesktopRef = useRef<HTMLDivElement>(null);
  const actionsDropdownMobileRef = useRef<HTMLDivElement>(null);

  const [totalUnreadNotifications, setTotalUnreadNotifications] = useState(0);

  const [businessServices, setBusinessServices] = useState<Array<{ id: string; name: string; price?: number | null }>>([]);
  const [openItemDropdownClientId, setOpenItemDropdownClientId] = useState<string | null>(null);
  const [itemDropdownAnchorRect, setItemDropdownAnchorRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const loadData = useCallback(async () => {
    if (!invoiceId) {
      toast.error('Invoice ID is missing');
      return;
    }
    if (!currentBusiness?.id) {
      // Business selection is resolved asynchronously; avoid firing a toast during initial load.
      if (!businessInitialized) return;
      setMissingBusiness(true);
      setLoading(false);
      return;
    }

    setMissingBusiness(false);
    setLoading(true);
    setNotFound(false);
    try {
      const inv = await apiService.getInvoice(invoiceId);
      if (!inv) {
        setNotFound(true);
        return;
      }

      setBillTo((inv.bill_to || '').trim() || 'Customer');
      setCustomerEmail((inv.customer_email || '').trim());
      setCustomerPhone(
        (inv as any).customer_phone
          ? displayCustomerPhone(String((inv as any).customer_phone).trim(), canViewFullCustomerPhone(user, currentBusiness?.id))
          : ''
      );
      const recEmail = (inv as any).receipt_email ? String((inv as any).receipt_email).trim() : '';
      setReceiptEmail(recEmail);
      setCollectionMethodDisplay(recEmail ? 'charge_now' : 'send_invoice');
      setInvoiceDate(toDateInputValue(inv.invoice_date));
      setDueDate(toDateInputValue(inv.due_date));
      const invStatus = (inv.status || 'DRAFT') as InvoiceStatus;
      setStatus(invStatus);
      setLoadedStatus(invStatus);
      setNotes((inv.notes || '').trim());
      setInternalNotes((inv.internal_notes || '').trim());
      setDiscount(moneyToCentsDigits(inv.discount));
      setIncludeTip(true);
      setTip(moneyToCentsDigits((inv as any).tip));
      setTipNote((inv as any).tip_note ?? '');
      setInvoiceDeposit(Math.max(0, safeNum((inv as any).deposit)));
      setInvoiceDue(Math.max(0, safeNum((inv as any).due)));
      setPaidAt((inv as any).paid_at ?? null);
      setInvoiceCreatedAt((inv as any).created_at ?? null);

      const linkedAppointmentId = String((inv as any).appointment_id ?? '').trim();
      if (linkedAppointmentId) {
        setInvoiceAppointmentId(linkedAppointmentId);
        try {
          const apt = await apiService.getAppointment(linkedAppointmentId);
          setInvoiceAppointmentScheduledAt(
            apt.scheduledAt ?? apt.flexScheduledAt?.earliest ?? null
          );
        } catch {
          setInvoiceAppointmentScheduledAt(null);
        }
      } else {
        setInvoiceAppointmentId(null);
        setInvoiceAppointmentScheduledAt(null);
      }

      const rawServices = inv.services;
      const arr = Array.isArray(rawServices) ? rawServices : (typeof rawServices === 'string' ? JSON.parse(rawServices || '[]') : []);
      const items: InvoiceLineItem[] = (arr || []).map((s: any, idx: number) => ({
        clientId: s.clientId || `item-${invoiceId}-${idx}-${Date.now()}`,
        included: !!s?.included,
        description: String(s?.description ?? '').trim() || 'Item',
        qty: Math.max(0, safeNum(s?.qty ?? 1)),
        unitPriceCents: moneyToCentsDigits(s?.unitPrice ?? 0),
        professionalName: s?.professionalName ?? undefined,
        serviceId: s?.serviceId,
        assignmentId: s?.assignmentId,
        durationMinutes: s?.durationMinutes,
        fromDateTime: s?.fromDateTime,
        toDateTime: s?.toDateTime,
        source: s?.source === 'booking' ? 'booking' : 'custom',
      }));
      const localSubTotal = items.reduce((sum, li) => {
        if (!li.included) return sum;
        const qty = Math.max(0, safeNum(li.qty));
        const price = Math.max(0, centsDigitsToAmount(li.unitPriceCents));
        return sum + qty * price;
      }, 0);
      const discountAmt = Math.max(0, safeNum(inv.discount));
      const afterDiscount = Math.max(0, localSubTotal - discountAmt);
      const taxAmt = Math.max(0, safeNum(inv.tax));
      const taxPct = afterDiscount > 0 ? (taxAmt / afterDiscount) * 100 : 0;
      setTaxPercent(percentToPercentDigits(taxPct));
      setLineItems(
        items.length > 0
          ? items
          : [{ clientId: `new-${Date.now()}`, included: true, description: '', qty: 1, unitPriceCents: '', source: 'custom' }]
      );
    } catch (e: any) {
      if (e?.response?.status === 404) setNotFound(true);
      else {
        console.error('Error loading invoice:', e);
        toast.error('Failed to load invoice');
      }
    } finally {
      setLoading(false);
      setLogsRefreshTrigger((t) => t + 1);
    }
  }, [invoiceId, currentBusiness?.id, businessInitialized]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    const loadUnread = async () => {
      if (!currentBusiness?.id) {
        if (!cancelled) setTotalUnreadNotifications(0);
        return;
      }
      try {
        const response = await apiService.getNotifications(currentBusiness.id, {
          page: 1,
          limit: 1,
          isRead: false,
        });
        if (!cancelled) setTotalUnreadNotifications(response.pagination?.total ?? 0);
      } catch {
        if (!cancelled) setTotalUnreadNotifications(0);
      }
    };
    loadUnread();
    return () => {
      cancelled = true;
    };
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (!currentBusiness?.id) return;
    let cancelled = false;
    apiService
      .getServices(currentBusiness.id)
      .then((list) => {
        if (!cancelled && Array.isArray(list)) {
          setBusinessServices(
            list
              .filter((s: any) => s?.deleted !== true)
              .map((s: any) => ({
                id: s.id,
                name: String(s.name ?? s.serviceName ?? '').trim(),
                price: s.price != null ? (Number.isFinite(Number(s.price)) ? Number(s.price) : null) : null,
              }))
              .filter((s) => s.name)
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (!currentBusiness?.id) {
      setOnlinePaymentsEnabled(false);
      return;
    }
    let cancelled = false;
    apiService
      .getBusinessStripeAccount(currentBusiness.id)
      .then((data) => {
        if (!cancelled) setOnlinePaymentsEnabled(!!(data?.chargesEnabled));
      })
      .catch(() => {
        if (!cancelled) setOnlinePaymentsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (!openItemDropdownClientId) {
      setItemDropdownAnchorRect(null);
      return;
    }
    const updateRect = () => {
      const candidates = Array.from(
        document.querySelectorAll(`[data-item-dropdown="${openItemDropdownClientId}"]`)
      );
      for (let i = 0; i < candidates.length; i++) {
        const r = candidates[i].getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setItemDropdownAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          return;
        }
      }
      setItemDropdownAnchorRect(null);
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
      setItemDropdownAnchorRect(null);
    };
  }, [openItemDropdownClientId]);

  useEffect(() => {
    if (!openItemDropdownClientId) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const list = document.querySelector('[data-item-dropdown-list]');
      if (list && list.contains(target)) return;
      const candidates = Array.from(
        document.querySelectorAll(`[data-item-dropdown="${openItemDropdownClientId}"]`)
      );
      for (let i = 0; i < candidates.length; i++) {
        if (candidates[i].contains(target)) return;
      }
      setOpenItemDropdownClientId(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [openItemDropdownClientId]);

  const { subTotal, total } = useMemo(() => {
    const subtotal = lineItems.reduce((sum, li) => {
      if (!li.included) return sum;
      const qty = Math.max(0, safeNum(li.qty));
      const price = Math.max(0, centsDigitsToAmount(li.unitPriceCents));
      return sum + qty * price;
    }, 0);
    const d = Math.max(0, centsDigitsToAmount(discount));
    const afterDiscount = Math.max(0, subtotal - d);
    const taxPct = Math.max(0, percentDigitsToPercent(taxPercent));
    const taxAmt = Math.round(afterDiscount * (taxPct / 100) * 100) / 100;
    const tipAmt = includeTip ? Math.max(0, centsDigitsToAmount(tip)) : 0;
    const computedTotal = afterDiscount + taxAmt + tipAmt;
    return { subTotal: subtotal, total: computedTotal };
  }, [lineItems, discount, taxPercent, includeTip, tip]);

  const computedDue = useMemo(() => Math.max(0, total - invoiceDeposit), [total, invoiceDeposit]);

  const canSave = useMemo(() => {
    if (!currentBusiness?.id) return false;
    if (!invoiceId) return false;
    if (!billTo.trim()) return false;
    const anyIncluded = lineItems.some((li) => li.included && safeNum(li.qty) > 0);
    return anyIncluded;
  }, [currentBusiness?.id, invoiceId, billTo, lineItems]);

  const canSendInvoice = useMemo(() => {
    const s = String(status || '').toUpperCase();
    return canSave && !saving && !['SENT', 'PAID', 'VOIDED'].includes(s) && !!customerEmail.trim();
  }, [canSave, saving, status, customerEmail]);

  const isReadOnly = useMemo(() => {
    const s = String(loadedStatus || '').toUpperCase();
    return s === 'PAID' || s === 'VOIDED';
  }, [loadedStatus]);

  const invoiceStatusUpper = useMemo(() => String(status || '').toUpperCase(), [status]);
  const showResendAction = invoiceStatusUpper === 'SENT';
  const showMarkPaidAction = invoiceStatusUpper === 'SENT' || invoiceStatusUpper === 'DRAFT';
  const showProcessPaymentAction = onlinePaymentsEnabled && !isReadOnly;
  const showCollectionMethodTabs = invoiceStatusUpper === 'DRAFT';
  const showPrimaryActionCard =
    !isReadOnly &&
    (collectionMethodDisplay === 'charge_now' || invoiceStatusUpper === 'DRAFT' || invoiceStatusUpper === 'SENT');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (actionsDropdownDesktopRef.current && actionsDropdownDesktopRef.current.contains(target)) ||
        (actionsDropdownMobileRef.current && actionsDropdownMobileRef.current.contains(target))
      ) {
        return;
      }
      setIsActionsDropdownOpen(false);
    };

    if (isActionsDropdownOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isActionsDropdownOpen]);

  /** Invoice is tied to an appointment if any line item came from a booking. */
  const isTiedToAppointment = useMemo(
    () => lineItems.some((li) => li.source === 'booking' || !!li.assignmentId),
    [lineItems]
  );
  /** Disable customer edit only when invoice is tied to an appointment; when not tied and status is DRAFT, allow edit. */
  const isCustomerSectionReadOnly = useMemo(
    () => isTiedToAppointment || String(loadedStatus || '').toUpperCase() !== 'DRAFT',
    [isTiedToAppointment, loadedStatus]
  );

  const formatCustomerEmail = useCallback((email: string | undefined | null): string => {
    if (!email) return 'No contact provided';
    if (email.startsWith('temp') && email.includes('@appointment.local')) return 'No contact provided';
    return email;
  }, []);
  const getCustomerPhone = useCallback((c: User | null | undefined): string | undefined => {
    const phone = c?.phone ?? (c as any)?.phoneNumber ?? (c as any)?.phone_number;
    if (typeof phone !== 'string' || !phone.trim()) return undefined;
    return displayCustomerPhone(phone, canViewFullCustomerPhone(user, currentBusiness?.id));
  }, [user, currentBusiness?.id]);

  const searchCustomers = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setCustomerSearchResults([]);
      return;
    }
    setSearchingCustomers(true);
    try {
      const backendResults = await apiService.searchUsers(query, 100, {
        businessId: currentBusiness?.id,
      });
      const fuzzyResults = fuzzySearchCustomers(backendResults, query);
      setCustomerSearchResults(fuzzyResults.slice(0, 10) as User[]);
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showNewCustomerFields && customerSearch && customerSearch.length >= 2) {
        searchCustomers(customerSearch);
      } else {
        setCustomerSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [showNewCustomerFields, customerSearch, searchCustomers]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerSearchContainerRef.current && !customerSearchContainerRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showNewCustomerFields || !customerEmail.trim() || !customerEmail.includes('@')) {
      setEmailLookupUser(null);
      return;
    }
    const timeoutId = setTimeout(async () => {
      const email = customerEmail.toLowerCase().trim();
      setCheckingEmail(true);
      try {
        const result = await apiService.checkEmailExists(email);
        if (result.exists && result.user) {
          const u = result.user;
          setEmailLookupUser({
            id: u.id,
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName,
            phoneNumber: (result.user as any).phoneNumber ?? (result.user as any).phone,
            roles: [],
            isEmailVerified: false,
            createdAt: '',
            updatedAt: '',
          } as User);
        } else {
          setEmailLookupUser(null);
        }
      } catch (err) {
        console.error('Error checking email:', err);
        setEmailLookupUser(null);
      } finally {
        setCheckingEmail(false);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [showNewCustomerFields, customerEmail]);

  const handleAddCustomLine = () => {
    const id = (crypto as any).randomUUID?.() || `${Date.now()}-${Math.random()}`;
    setLineItems((prev) => [
      ...prev,
      {
        clientId: id,
        included: true,
        description: '',
        qty: 1,
        unitPriceCents: '',
        source: 'custom',
      },
    ]);
  };

  const updateLine = (clientId: string, patch: Partial<InvoiceLineItem>) => {
    setLineItems((prev) => prev.map((li) => (li.clientId === clientId ? { ...li, ...patch } : li)));
  };

  const removeLine = (clientId: string) => {
    setLineItems((prev) => prev.filter((li) => li.clientId !== clientId));
  };

  const handleSave = async (
    forceStatus?: InvoiceStatus,
    options?: { silent?: boolean; reload?: boolean }
  ): Promise<boolean> => {
    if (!currentBusiness?.id || !invoiceId) {
      toast.error('Missing business or invoice');
      return false;
    }
    if (!canSave) {
      toast.error('Please include at least one line item and complete required fields');
      return false;
    }

    setSaving(true);
    try {
      const items = lineItems.map((li) => {
        const qty = Math.max(0, safeNum(li.qty));
        const unitPrice = Math.max(0, centsDigitsToAmount(li.unitPriceCents));
        const amount = li.included ? qty * unitPrice : 0;
        return {
          included: !!li.included,
          description: (li.description || '').trim(),
          qty,
          unitPrice,
          amount,
          professionalName: li.professionalName || null,
          serviceId: li.serviceId || null,
          assignmentId: li.assignmentId || null,
          durationMinutes: li.durationMinutes ?? null,
          fromDateTime: li.fromDateTime ?? null,
          toDateTime: li.toDateTime ?? null,
          source: li.source || 'custom',
        };
      });

      const subTotalAmt = items.reduce((sum, s) => sum + safeNum((s as any).amount), 0);
      const discountAmt = Math.max(0, centsDigitsToAmount(discount));
      const afterDiscount = Math.max(0, subTotalAmt - discountAmt);
      const taxPct = Math.max(0, percentDigitsToPercent(taxPercent));
      const taxAmt = Math.round(afterDiscount * (taxPct / 100) * 100) / 100;
      const tipAmt = includeTip ? Math.max(0, centsDigitsToAmount(tip)) : 0;

      await apiService.updateInvoice(invoiceId, {
        billTo: billTo.trim(),
        customerEmail: customerEmail.trim() || null,
        customerPhone: customerPhone.trim() || null,
        receiptEmail: receiptEmail.trim() || null,
        invoiceDate,
        dueDate,
        status: toApiInvoiceStatus(forceStatus ?? loadedStatus),
        notes: notes.trim() || null,
        internalNotes: internalNotes.trim() || null,
        discount: discountAmt,
        tax: taxAmt, // server expects amount, UI captures percent
        tip: tipAmt,
        tipNote: includeTip ? (tipNote.trim() || null) : null,
        services: items,
      });
      if (!options?.silent) toast.success('Invoice updated');
      if (options?.reload !== false) loadData();
      return true;
    } catch (e: any) {
      console.error('Error updating invoice:', e);
      toast.error(e?.response?.data?.error || 'Failed to update invoice');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!invoiceId) {
      toast.error('Invoice ID is missing');
      return;
    }
    if (!customerEmail.trim()) {
      toast.error('Customer email is required to send the invoice');
      return;
    }

    // Save any edits first, but let the server mark invoice as SENT only after email succeeds.
    const saved = await handleSave(undefined, { silent: true, reload: false });
    if (!saved) return;

    setSaving(true);
    try {
      await apiService.sendInvoiceEmail(invoiceId);
      toast.success(`Invoice emailed to ${customerEmail.trim()}`);
      loadData();
    } catch (e: any) {
      console.error('Error sending invoice email:', e);
      toast.error(e?.response?.data?.error || 'Failed to send invoice email');
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvoice = async () => {
    if (!invoiceId) {
      toast.error('Invoice ID is missing');
      return;
    }
    if (!customerEmail.trim()) {
      toast.error('Customer email is required to resend the invoice');
      return;
    }
    // Save any edits first so the resent email reflects current data.
    const saved = await handleSave(undefined, { silent: true, reload: false });
    if (!saved) return;
    setSaving(true);
    try {
      await apiService.sendInvoiceEmail(invoiceId);
      toast.success(`Invoice resent to ${customerEmail.trim()}`);
      loadData();
    } catch (e: any) {
      console.error('Error resending invoice email:', e);
      toast.error(e?.response?.data?.error || 'Failed to resend invoice email');
    } finally {
      setSaving(false);
    }
  };

  const handleContinueToPayment = async () => {
    if (!invoiceId) return;
    const saved = await handleSave(undefined, { silent: true, reload: false });
    if (!saved) return;
    navigate(`/dashboard/business/invoices/${invoiceId}/charge`, { state: { fromDetails: true } });
  };

  const handleMarkInvoicePaidSubmit = async (payment: {
    paymentMethod: string;
    paymentDate: string;
    referenceNumber?: string | null;
    notes?: string | null;
  }) => {
    if (!invoiceId) return;
    try {
      await apiService.markInvoicePaid(invoiceId, payment);
      toast.success('Invoice marked as paid');
      setMarkPaidModalOpen(false);
      loadData();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to mark invoice as paid');
      throw e;
    }
  };

  const handleVoidInvoice = async () => {
    if (!invoiceId) return;
    setVoidConfirmOpen(false);
    setSaving(true);
    const t = toast.loading('Voiding invoice…');
    try {
      await apiService.updateInvoice(invoiceId, { status: 'VOIDED' });
      toast.dismiss(t);
      toast.success('Invoice voided');
      loadData();
    } catch (e: any) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.error || 'Failed to void invoice');
    } finally {
      setSaving(false);
    }
  };

  const closeActionsMenu = () => setIsActionsDropdownOpen(false);

  const renderInvoiceActionsMenu = () => (
    <>
      {showProcessPaymentAction ? (
        <button
          type="button"
          onClick={() => {
            closeActionsMenu();
            void handleContinueToPayment();
          }}
          disabled={saving}
          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="bi bi-credit-card text-green-600"></i>
          Process Payment
        </button>
      ) : null}
      {showResendAction ? (
        <button
          type="button"
          onClick={() => {
            closeActionsMenu();
            void handleResendInvoice();
          }}
          disabled={!customerEmail.trim() || saving}
          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="bi bi-send text-amber-600"></i>
          Resend Invoice
        </button>
      ) : null}
      {showMarkPaidAction ? (
        <button
          type="button"
          onClick={() => {
            closeActionsMenu();
            setMarkPaidModalOpen(true);
          }}
          disabled={saving || computedDue <= 0}
          className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="bi bi-cash-coin text-green-600"></i>
          Mark as Paid
        </button>
      ) : null}
      {showProcessPaymentAction || showResendAction || showMarkPaidAction ? (
        <div className="h-px bg-gray-200 my-2"></div>
      ) : null}
      <button
        type="button"
        onClick={() => {
          closeActionsMenu();
          setVoidConfirmOpen(true);
        }}
        disabled={saving}
        className="w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-sm font-semibold text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <i className="bi bi-slash-circle"></i>
        Void Invoice
      </button>
    </>
  );

  const goBack = () => {
    navigate('/dashboard/business?section=invoices');
  };

  const handleNotificationsClick = () => {
    navigate('/dashboard/business?section=notifications');
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <p className="text-gray-700 font-semibold">Invoice not found</p>
        <button onClick={goBack} className="mt-4 aacore-btn2-outline">
          Back to invoices
        </button>
      </div>
    );
  }

  if (missingBusiness) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center">
          <div className="text-lg font-semibold text-gray-900">No business selected</div>
          <div className="text-sm text-gray-600 mt-2">
            Please select a business (or ensure your business role is active) to view this invoice.
          </div>
          <button onClick={goBack} className="mt-5 aacore-btn2-outline">
            Back to invoices
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar
        unreadNotificationsCount={totalUnreadNotifications}
        onNotificationsClick={handleNotificationsClick}
      />

      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="invoices"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={toggleSidebar}
            unreadNotificationCount={totalUnreadNotifications}
          />
        )}

        <div className="flex flex-col min-h-screen">
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-md hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Invoice details</h1>
              {!isReadOnly ? (
                <div className="relative" ref={actionsDropdownMobileRef}>
                  <button
                    type="button"
                    onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
                    className="p-2 rounded-md hover:bg-gray-100 flex items-center justify-center"
                    title="Actions"
                    aria-label="Actions"
                  >
                    <Settings className="w-8 h-8 text-gray-700 -m-1" />
                  </button>
                  {isActionsDropdownOpen ? (
                    <div className="absolute right-0 top-full mt-2 min-w-[240px] bg-white border border-gray-300 rounded-lg shadow-lg z-30 py-2">
                      {renderInvoiceActionsMenu()}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="w-10"></div>
              )}
            </div>
          </div>

          <div className="flex-1 p-4 md:p-8">
            <button onClick={goBack} className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2" disabled={saving}>
              <ArrowLeft className="w-4 h-4" />
              Back to invoices
            </button>

            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="hidden lg:block text-2xl font-semibold text-gray-900">
                  Invoice details
                </h1>
                <div className="text-sm text-gray-600 mt-1">
                  {(() => {
                    const s = String(status || '').toUpperCase();
                    if (s === 'PAID') return 'This invoice is paid and cannot be edited.';
                    if (s === 'VOIDED') return 'This invoice is voided and cannot be edited.';
                    if (s === 'SENT') return 'This invoice has not been paid yet.';
                    return 'View and edit this invoice. Send by email or collect payment now.';
                  })()}
                </div>
              </div>
              {!isReadOnly ? (
                <div className="relative hidden lg:block shrink-0" ref={actionsDropdownDesktopRef}>
                  <button
                    type="button"
                    onClick={() => setIsActionsDropdownOpen(!isActionsDropdownOpen)}
                    className="aacore-btn2-primary"
                  >
                    Actions
                    <i className={`bi bi-chevron-down transition-transform duration-200 ${isActionsDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {isActionsDropdownOpen ? (
                    <div className="absolute right-0 top-full mt-2 min-w-[240px] bg-white border border-gray-300 rounded-lg shadow-lg z-30 py-2">
                      {renderInvoiceActionsMenu()}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <UserIcon className="w-5 h-5 text-gray-600" />
                    Customer
                  </h2>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    {(billTo.trim() || customerEmail.trim() || customerPhone.trim()) && !showNewCustomerFields && (
                      <div className="flex-1 flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-[#0B4778] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                          {(() => {
                            const parts = billTo.trim().split(/\s+/).filter(Boolean);
                            const a = parts[0]?.[0] ?? '';
                            const b = parts[1]?.[0] ?? (billTo.trim()[1] ?? '');
                            return (a + b).toUpperCase() || '?';
                          })()}
                        </div>
                        <div className="min-w-0 flex-1">
                          {billTo.trim() && <p className="font-medium text-gray-900 truncate">{billTo}</p>}
                          {customerEmail.trim() && <p className="text-sm text-gray-600 truncate">{customerEmail}</p>}
                          {customerPhone.trim() && <p className="text-sm text-gray-600 truncate">{customerPhone}</p>}
                        </div>
                        {!isCustomerSectionReadOnly && (
                          <button
                            type="button"
                            onClick={() => {
                              setBillTo('');
                              setCustomerEmail('');
                              setCustomerPhone('');
                              setEmailLookupUser(null);
                              setShowNewCustomerFields(true);
                            }}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            aria-label="Clear customer"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    )}
                    {showNewCustomerFields && !isCustomerSectionReadOnly && (
                      <div className="flex-1 relative" ref={customerSearchContainerRef}>
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="customer-search-query"
                          placeholder="Search existing customer by name, phone or email"
                          value={customerSearch}
                          readOnly={searchInputReadOnly}
                          onFocus={() => {
                            if (searchInputReadOnly) setSearchInputReadOnly(false);
                            setShowCustomerDropdown(true);
                          }}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            setShowCustomerDropdown(true);
                          }}
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          data-lpignore="true"
                          data-form-type="other"
                          className={`w-full pl-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 ${customerSearch ? 'pr-10' : 'pr-3'}`}
                          disabled={saving}
                        />
                        {customerSearch && (
                          <button
                            type="button"
                            onClick={() => {
                              setCustomerSearch('');
                              setCustomerSearchResults([]);
                              setShowCustomerDropdown(false);
                              setSearchInputReadOnly(true);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label="Clear search"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {showCustomerDropdown && (customerSearchResults.length > 0 || searchingCustomers) && (
                          <div className="absolute z-50 w-full mt-1 left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                            {searchingCustomers ? (
                              <div className="px-4 py-3 text-gray-500 text-sm">Searching...</div>
                            ) : (
                              customerSearchResults.map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={() => {
                                    setBillTo(`${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Customer');
                                    setCustomerEmail(customer.email || '');
                                    setCustomerPhone(getCustomerPhone(customer) || '');
                                    if (collectionMethodDisplay === 'charge_now') setReceiptEmail(customer.email || '');
                                    setShowNewCustomerFields(false);
                                    setCustomerSearch('');
                                    setShowCustomerDropdown(false);
                                    setCustomerSearchResults([]);
                                  }}
                                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="w-8 h-8 rounded-full bg-[#0B4778] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                                    {(customer.firstName?.[0] ?? '') + (customer.lastName?.[0] ?? '')}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{customer.firstName} {customer.lastName}</p>
                                    <p className="text-sm text-gray-600 truncate">{formatCustomerEmail(customer.email)}</p>
                                    {getCustomerPhone(customer) && (
                                      <p className="text-sm text-gray-600 truncate">{getCustomerPhone(customer)}</p>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                        {showCustomerDropdown && customerSearch.length >= 2 && !searchingCustomers && customerSearchResults.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg px-4 py-3 text-gray-500 text-sm">
                            No customers found.
                          </div>
                        )}
                      </div>
                    )}
                    {!isCustomerSectionReadOnly && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewCustomerFields(true);
                          setBillTo('');
                          setCustomerEmail('');
                          setCustomerPhone('');
                          setCustomerSearch('');
                          setCustomerSearchResults([]);
                          setShowCustomerDropdown(false);
                          setSearchInputReadOnly(true);
                          setEmailLookupUser(null);
                        }}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white text-[#0B4778] font-semibold hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 shrink-0"
                        disabled={saving}
                      >
                        <UserPlus className="w-4 h-4" />
                        New Customer
                      </button>
                    )}
                  </div>
                  {showNewCustomerFields && (
                    <div className="mb-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Customer Name</label>
                          <input
                            className={`w-full px-3 py-2 border rounded-lg ${isCustomerSectionReadOnly ? 'border-gray-200 bg-gray-50 text-gray-900 cursor-not-allowed' : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                            value={billTo}
                            readOnly={isCustomerSectionReadOnly}
                            disabled={isCustomerSectionReadOnly}
                            aria-readonly={isCustomerSectionReadOnly}
                            onChange={(e) => !isCustomerSectionReadOnly && setBillTo(e.target.value)}
                          />
                        </div>
                        <div className="relative">
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Customer email</label>
                          <input
                            type="email"
                            className={`w-full px-3 py-2 border rounded-lg ${isCustomerSectionReadOnly ? 'border-gray-200 bg-gray-50 text-gray-900 cursor-not-allowed' : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                            value={customerEmail}
                            readOnly={isCustomerSectionReadOnly}
                            disabled={isCustomerSectionReadOnly}
                            aria-readonly={isCustomerSectionReadOnly}
                            onChange={(e) => !isCustomerSectionReadOnly && setCustomerEmail(e.target.value)}
                          />
                          {checkingEmail && (
                            <div className="absolute right-3 top-9 -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-[#0B4778]" />
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Customer Phone</label>
                          <input
                            type="tel"
                            className={`w-full px-3 py-2 border rounded-lg ${isCustomerSectionReadOnly ? 'border-gray-200 bg-gray-50 text-gray-900 cursor-not-allowed' : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                            value={customerPhone}
                            readOnly={isCustomerSectionReadOnly}
                            disabled={isCustomerSectionReadOnly}
                            aria-readonly={isCustomerSectionReadOnly}
                            placeholder="Optional"
                            onChange={(e) => !isCustomerSectionReadOnly && setCustomerPhone(e.target.value)}
                          />
                        </div>
                      </div>
                      {emailLookupUser && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                          <p className="text-sm text-amber-800 mb-2">
                            This email is associated with an existing customer. Select them below or enter a different email address.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              const u = emailLookupUser;
                              setBillTo(`${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Customer');
                              setCustomerEmail(u.email || '');
                              setCustomerPhone(getCustomerPhone(u) || '');
                              if (collectionMethodDisplay === 'charge_now') setReceiptEmail(u.email || '');
                              setEmailLookupUser(null);
                              setShowNewCustomerFields(false);
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-lg border border-amber-300 bg-white hover:bg-amber-50/50 text-left"
                          >
                            <div className="w-10 h-10 rounded-full bg-[#0B4778] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                              {emailLookupUser.firstName?.[0]}{emailLookupUser.lastName?.[0]}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900">
                                {emailLookupUser.firstName} {emailLookupUser.lastName}
                              </p>
                              <p className="text-sm text-gray-600 truncate">{formatCustomerEmail(emailLookupUser.email)}</p>
                              {getCustomerPhone(emailLookupUser) && (
                                <p className="text-sm text-gray-500 truncate">{getCustomerPhone(emailLookupUser)}</p>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-[#0B4778] shrink-0">Select</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    Invoice Details
                  </h2>
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <label className="text-sm font-semibold text-gray-700">Status</label>
                    <span
                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${
                        status === 'PAID'
                          ? 'bg-green-100 text-green-800'
                          : status === 'SENT'
                            ? 'bg-blue-100 text-blue-800'
                            : status === 'VOIDED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {(status || '').split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                    </span>
                  </div>
                  {String(status || '').toUpperCase() === 'PAID' ? (
                    <div className="mb-4 flex flex-wrap gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice Date</label>
                        <div className="text-gray-900">{invoiceDate ? formatDateOnly(invoiceDate) : '—'}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Paid On</label>
                        <div className="text-gray-900">
                          {paidAt ? formatDateOnly(toLocalYmd(new Date(paidAt))) : '—'}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Receipt Email</label>
                        <div className="text-gray-900">{receiptEmail.trim() || customerEmail.trim() || '—'}</div>
                      </div>
                    </div>
                  ) : invoiceStatusUpper === 'SENT' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 disabled:cursor-not-allowed"
                          value={invoiceDate}
                          disabled
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                  ) : invoiceStatusUpper === 'VOIDED' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 disabled:cursor-not-allowed"
                          value={invoiceDate}
                          disabled
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 disabled:cursor-not-allowed"
                          value={dueDate}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      {showCollectionMethodTabs ? (
                        <div className="mb-4">
                          <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-2">
                            Collection Method
                            <span className="text-gray-400" title="Choose how to collect payment: send an invoice by email or charge the customer now.">
                              <HelpCircle className="w-4 h-4" />
                            </span>
                          </label>
                          <div
                            role="tablist"
                            aria-label="Collection method"
                            className="flex w-full rounded-lg bg-gray-100 p-1 overflow-hidden"
                          >
                            <button
                              type="button"
                              role="tab"
                              aria-selected={collectionMethodDisplay === 'send_invoice'}
                              onClick={() => !isReadOnly && setCollectionMethodDisplay('send_invoice')}
                              style={collectionTabClipLeft}
                              className={`flex-1 min-w-0 py-2.5 pl-4 pr-6 rounded-l-md text-sm font-semibold transition-all duration-150 inline-flex items-center justify-center gap-2 relative ${
                                collectionMethodDisplay === 'send_invoice'
                                  ? 'bg-[#0B4778] text-white z-10'
                                  : 'text-gray-600 hover:text-gray-800 z-0'
                              } ${isReadOnly ? 'cursor-default' : ''}`}
                              disabled={saving}
                            >
                              <Send className="w-4 h-4 shrink-0" />
                              Send Invoice
                            </button>
                            {!onlinePaymentsEnabled ? (
                              <InfoTooltip
                                triggerClassName="flex-1 flex min-w-0 -ml-4"
                                content={
                                  <>
                                    <span className="font-semibold">Payment processing is not available.</span>
                                    <br />
                                    Finish your payment setup before trying to charge a customer on the spot.
                                  </>
                                }
                              >
                                <button
                                  type="button"
                                  role="tab"
                                  aria-selected={false}
                                  aria-disabled="true"
                                  onClick={() => {}}
                                  style={collectionTabClipRight}
                                  className="w-full min-w-0 py-2.5 pl-6 pr-4 rounded-r-md text-sm font-semibold transition-all duration-150 inline-flex items-center justify-center gap-2 text-gray-400 cursor-not-allowed relative z-0"
                                  disabled
                                >
                                  <CreditCard className="w-4 h-4 shrink-0" />
                                  Charge Now
                                </button>
                              </InfoTooltip>
                            ) : (
                              <button
                                type="button"
                                role="tab"
                                aria-selected={collectionMethodDisplay === 'charge_now'}
                                onClick={() => {
                                  if (isReadOnly) return;
                                  setCollectionMethodDisplay('charge_now');
                                  if (!receiptEmail.trim()) setReceiptEmail(customerEmail);
                                }}
                                style={collectionTabClipRight}
                                className={`flex-1 min-w-0 -ml-4 py-2.5 pl-6 pr-4 rounded-r-md text-sm font-semibold transition-all duration-150 inline-flex items-center justify-center gap-2 relative ${
                                  collectionMethodDisplay === 'charge_now'
                                    ? 'bg-[#0B4778] text-white z-10'
                                    : 'text-gray-600 hover:text-gray-800 z-0'
                                } ${isReadOnly ? 'cursor-default' : ''}`}
                                disabled={saving}
                              >
                                <CreditCard className="w-4 h-4 shrink-0" />
                                Charge Now
                              </button>
                            )}
                          </div>
                          {!onlinePaymentsEnabled && (
                            <p className="mt-2 text-xs text-amber-600 inline-flex items-center gap-1">
                              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                              Payment not enabled — finish payment setup to charge on the spot.
                            </p>
                          )}
                        </div>
                      ) : null}
                      {collectionMethodDisplay === 'send_invoice' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                          value={invoiceDate}
                          onChange={(e) => setInvoiceDate(e.target.value)}
                          disabled={saving || isReadOnly}
                          readOnly={isReadOnly}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          disabled={saving || isReadOnly}
                          readOnly={isReadOnly}
                        />
                      </div>
                    </div>
                  )}
                  {collectionMethodDisplay === 'charge_now' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                          value={invoiceDate}
                          onChange={(e) => setInvoiceDate(e.target.value)}
                          disabled={saving || isReadOnly}
                          readOnly={isReadOnly}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Receipt Email</label>
                        <input
                          type="email"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                          placeholder="Email to receive a copy of the paid invoice"
                          value={receiptEmail}
                          onChange={(e) => setReceiptEmail(e.target.value)}
                          disabled={saving || isReadOnly}
                          readOnly={isReadOnly}
                        />
                        <p className="text-xs text-gray-500 mt-1">A copy of the paid invoice will be sent to this email.</p>
                      </div>
                    </div>
                  )}
                    </>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <ListOrdered className="w-5 h-5 text-gray-600" />
                      Line items
                    </h2>
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={handleAddCustomLine}
                        className="aacore-btn2-secondary inline-flex items-center gap-2 text-sm disabled:opacity-50"
                        disabled={saving}
                      >
                        <Plus className="w-4 h-4" />
                        Add item
                      </button>
                    )}
                  </div>

                  {lineItems.length === 0 && (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                      Add at least one line item before an invoice can be saved.
                    </div>
                  )}

                  {/* Mobile: card layout so item name, qty and price are never cut off */}
                  <div className="md:hidden space-y-4">
                    {lineItems.map((li) => {
                      const amount = li.included ? Math.max(0, safeNum(li.qty)) * Math.max(0, centsDigitsToAmount(li.unitPriceCents)) : 0;
                      const showDetails = !!(li.professionalName || li.source === 'booking');
                      return (
                        <div
                          key={li.clientId}
                          className={`border rounded-lg p-4 space-y-3 ${li.included ? 'border-gray-200 bg-gray-50/50' : 'border-gray-200 bg-gray-100/80 opacity-70'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center shrink-0">
                              <input
                                type="checkbox"
                                checked={li.included}
                                onChange={(e) => updateLine(li.clientId, { included: e.target.checked })}
                                disabled={saving || isReadOnly}
                                className="h-4 w-4 accent-[#0B4778]"
                                aria-label="Include in invoice"
                              />
                            </div>
                            <div
                              className="flex-1 min-w-0 relative"
                              data-item-dropdown={li.clientId}
                            >
                              <input
                                className="w-full min-w-0 h-10 pl-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-semibold disabled:bg-gray-50 disabled:cursor-not-allowed"
                                value={li.description}
                                onChange={(e) => updateLine(li.clientId, { description: e.target.value })}
                                onFocus={() => !isReadOnly && setOpenItemDropdownClientId(li.clientId)}
                                disabled={saving || isReadOnly}
                                readOnly={isReadOnly}
                                placeholder="Enter/search item name"
                              />
                              {!isReadOnly ? (
                                <button
                                  type="button"
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-0.5 rounded"
                                  onClick={() => setOpenItemDropdownClientId(li.clientId)}
                                  disabled={saving}
                                  aria-label="Show services"
                                >
                                  <ChevronDown className="w-5 h-5" />
                                </button>
                              ) : (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                  <ChevronDown className="w-5 h-5" />
                                </div>
                              )}
                            </div>
                            {!isReadOnly && (
                              <div className="shrink-0">
                                <button
                                  type="button"
                                  onClick={() => removeLine(li.clientId)}
                                  className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-red-600 transition-colors hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-50"
                                  disabled={saving}
                                  title="Remove"
                                  aria-label="Remove line item"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Qty</label>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                placeholder="0"
                                value={li.qty === 0 ? '' : li.qty}
                                onChange={(e) => updateLine(li.clientId, { qty: e.target.value === '' ? 0 : safeNum(e.target.value) })}
                                disabled={saving || isReadOnly}
                                readOnly={isReadOnly}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Unit price</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  className={`w-full h-10 pl-7 pr-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed ${!li.unitPriceCents || centsDigitsToAmount(li.unitPriceCents) === 0 ? 'bg-orange-50 border-amber-300' : 'border-gray-300'}`}
                                  placeholder="0.00"
                                  value={centsDigitsToMoneyString(li.unitPriceCents)}
                                  onChange={(e) => updateLine(li.clientId, { unitPriceCents: e.target.value.replace(/\D/g, '') })}
                                  disabled={saving || isReadOnly}
                                  readOnly={isReadOnly}
                                />
                              </div>
                            </div>
                            <div className="text-right">
                              <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
                              <div className="h-10 flex items-center justify-end font-semibold text-gray-900">
                                {li.included ? `$${formatMoney(amount)}` : '—'}
                              </div>
                            </div>
                          </div>
                          {showDetails && (
                            <div className="text-xs text-gray-500 pt-1 border-t border-gray-100">
                              {li.source === 'booking' ? 'From booking' : 'Custom'}
                              {li.professionalName ? ` • Professional: ${li.professionalName}` : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop: table layout */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-200">
                          <th className="py-2 pl-2 pr-3 w-16">Inc</th>
                          <th className="py-2 pr-3">Item</th>
                          <th className="py-2 pr-3 w-24">Qty</th>
                          <th className="py-2 pr-3 w-32">Unit</th>
                          <th className="py-2 pr-3 w-32 text-right">Amount</th>
                          <th className="py-2 w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li) => {
                          const amount = li.included ? Math.max(0, safeNum(li.qty)) * Math.max(0, centsDigitsToAmount(li.unitPriceCents)) : 0;
                          const showDetails = !!(li.professionalName || li.source === 'booking');
                          return (
                            <React.Fragment key={li.clientId}>
                              <tr
                                className={`${showDetails ? '' : 'border-b border-gray-100'} align-middle ${!li.included ? 'bg-gray-100/80 opacity-70' : ''}`}
                              >
                                <td className="py-2 pl-2 pr-3">
                                  <div className="h-10 flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={li.included}
                                      onChange={(e) => updateLine(li.clientId, { included: e.target.checked })}
                                      disabled={saving || isReadOnly}
                                      className="h-4 w-4 accent-[#0B4778]"
                                    />
                                  </div>
                                </td>
                                <td className="py-2 pr-3 align-top">
                                  <div
                                    className="relative"
                                    data-item-dropdown={li.clientId}
                                  >
                                    <input
                                      className="w-full h-10 pl-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-semibold disabled:bg-gray-50 disabled:cursor-not-allowed"
                                      value={li.description}
                                      onChange={(e) => updateLine(li.clientId, { description: e.target.value })}
                                      onFocus={() => !isReadOnly && setOpenItemDropdownClientId(li.clientId)}
                                      disabled={saving || isReadOnly}
                                      readOnly={isReadOnly}
                                      placeholder="Enter/search item name"
                                    />
                                    {!isReadOnly ? (
                                      <button
                                        type="button"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-0.5 rounded"
                                        onClick={() => setOpenItemDropdownClientId(li.clientId)}
                                        disabled={saving}
                                        aria-label="Show services"
                                      >
                                        <ChevronDown className="w-5 h-5" />
                                      </button>
                                    ) : (
                                      <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                                        <ChevronDown className="w-5 h-5" />
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="py-2 pr-3">
                                  <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                    placeholder="0"
                                    value={li.qty === 0 ? '' : li.qty}
                                    onChange={(e) => updateLine(li.clientId, { qty: e.target.value === '' ? 0 : safeNum(e.target.value) })}
                                    disabled={saving || isReadOnly}
                                    readOnly={isReadOnly}
                                  />
                                </td>
                                <td className="py-2 pr-3">
                                  <div className="relative h-10">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      className={`w-full h-10 pl-7 pr-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed ${!li.unitPriceCents || centsDigitsToAmount(li.unitPriceCents) === 0 ? 'bg-orange-50 border-amber-300' : 'border-gray-300'}`}
                                      placeholder="0.00"
                                      value={centsDigitsToMoneyString(li.unitPriceCents)}
                                      onChange={(e) => updateLine(li.clientId, { unitPriceCents: e.target.value.replace(/\D/g, '') })}
                                      disabled={saving || isReadOnly}
                                      readOnly={isReadOnly}
                                    />
                                  </div>
                                </td>
                                <td className="py-2 pr-3 text-right">
                                  <div className="h-10 flex items-center justify-end font-semibold text-gray-900 whitespace-nowrap">
                                    {li.included ? `$${formatMoney(amount)}` : '—'}
                                  </div>
                                </td>
                                <td className="py-2 text-right">
                                  <div className="h-10 flex items-center justify-end">
                                    {!isReadOnly && (
                                      <button
                                        type="button"
                                        onClick={() => removeLine(li.clientId)}
                                        className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-red-600 transition-colors hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-50"
                                        disabled={saving}
                                        title="Remove"
                                        aria-label="Remove line item"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>

                              {showDetails && (
                                <tr className="border-b border-gray-100">
                                  <td className="py-0 pl-2 pr-3" />
                                  <td className="pb-3 pr-3" colSpan={5}>
                                    <div className="text-xs text-gray-500 -mt-1">
                                      {li.source === 'booking' ? 'From booking' : 'Custom'}
                                      {li.professionalName ? ` • Professional: ${li.professionalName}` : ''}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="text-xs text-gray-500 mt-3">
                    Tip: prices are editable for the invoice and may differ from booking estimates.
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <StickyNote className="w-5 h-5 text-gray-600" />
                    Notes
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Customer note (shows on invoice)</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={saving || isReadOnly}
                        readOnly={isReadOnly}
                      />
                      {!onlinePaymentsEnabled &&
                        !['PAID', 'VOIDED'].includes(String(loadedStatus || '').toUpperCase()) && (
                        <div
                          role="alert"
                          className="mt-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-amber-900"
                        >
                          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                          </div>
                          <div className="min-w-0 text-xs leading-relaxed text-amber-900">
                            <p className="text-sm font-semibold text-amber-950">Online payments are not enabled</p>
                            <p className="mt-1">
                              Stripe has not been set up for this business. AACore Booking can send this invoice, but the
                              customer will not be able to pay online through the platform.
                            </p>
                            <p className="mt-2">
                              Consider adding payment options in the customer note above before sending the invoice.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Internal note (staff only)</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                        rows={3}
                        value={internalNotes}
                        onChange={(e) => setInternalNotes(e.target.value)}
                        disabled={saving || isReadOnly}
                        readOnly={isReadOnly}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-gray-600" />
                    Totals
                  </h2>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">Subtotal</div>
                      <div className="font-semibold text-gray-900">${formatMoney(subTotal)}</div>
                    </div>
                    <div className="grid grid-cols-2 items-center gap-3">
                      <div className="text-sm text-gray-600">Discount</div>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0.00"
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right disabled:bg-gray-50 disabled:cursor-not-allowed"
                          value={centsDigitsToMoneyString(discount)}
                          onChange={(e) => setDiscount(e.target.value.replace(/\D/g, ''))}
                          disabled={saving || isReadOnly}
                          readOnly={isReadOnly}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 items-center gap-3">
                      <div className="text-sm text-gray-600">Tax (%)</div>
                      <div className="relative">
                        <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0.00"
                          className="w-full pr-7 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right disabled:bg-gray-50 disabled:cursor-not-allowed"
                          value={percentDigitsToPercentString(taxPercent)}
                          onChange={(e) => setTaxPercent(e.target.value.replace(/\D/g, ''))}
                          disabled={saving || isReadOnly}
                          readOnly={isReadOnly}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 items-center gap-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="include-tip-details"
                          checked={includeTip}
                          onChange={(e) => setIncludeTip(e.target.checked)}
                          disabled={saving || isReadOnly}
                          className="h-4 w-4 accent-[#0B4778]"
                        />
                        <label htmlFor="include-tip-details" className="text-sm text-gray-600">Tip</label>
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0.00"
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right disabled:bg-gray-100"
                          value={centsDigitsToMoneyString(tip)}
                          onChange={(e) => setTip(e.target.value.replace(/\D/g, ''))}
                          disabled={saving || !includeTip || isReadOnly}
                          readOnly={isReadOnly}
                        />
                      </div>
                    </div>
                    <div className="h-px bg-gray-200 my-2"></div>
                    <div className="flex items-center justify-between">
                      <div className="text-base font-semibold text-gray-900">Total</div>
                      <div className="text-2xl font-semibold text-gray-900">${formatMoney(total)}</div>
                    </div>
                    {invoiceDeposit > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">Deposit</div>
                        <div className="font-semibold text-gray-900">-${formatMoney(invoiceDeposit)}</div>
                      </div>
                    )}
                    <div className="rounded-lg px-4 py-3 mt-2 bg-[#0B4778]/10 border border-[#0B4778]/20">
                      <div className="flex items-center justify-between">
                        <div className="text-base font-semibold text-[#0B4778]">Invoice Due</div>
                        <div className="text-xl font-bold text-[#0B4778]">${formatMoney(computedDue)}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <InvoiceLogsSection
                  invoiceId={invoiceId}
                  invoiceCreatedAt={invoiceCreatedAt}
                  appointmentContext={
                    invoiceAppointmentId
                      ? { id: invoiceAppointmentId, scheduledAt: invoiceAppointmentScheduledAt }
                      : null
                  }
                  refreshTrigger={logsRefreshTrigger}
                />

                {showPrimaryActionCard ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                    {collectionMethodDisplay === 'charge_now' ? (
                      <button
                        type="button"
                        onClick={handleContinueToPayment}
                        className="aacore-btn2-primary inline-flex items-center justify-center gap-2 w-full disabled:opacity-50"
                        disabled={!canSave || saving}
                        title={saving ? 'Saving…' : !canSave ? 'Include at least one line item and required fields' : 'Continue to payment to charge the customer'}
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                        ) : null}
                        Continue to Payment
                        {!saving ? <ChevronRight className="w-4 h-4 shrink-0" /> : null}
                      </button>
                    ) : invoiceStatusUpper === 'SENT' ? (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={handleResendInvoice}
                            className="aacore-btn2-primary inline-flex items-center justify-center gap-2 w-full disabled:opacity-50"
                            disabled={!customerEmail.trim() || saving}
                            title={saving ? 'Resending…' : !customerEmail.trim() ? 'Customer email is required to resend' : 'Resend invoice by email'}
                          >
                            {saving ? (
                              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                            ) : (
                              <Send className="w-4 h-4 shrink-0" />
                            )}
                            Resend Invoice
                          </button>
                          <button
                            type="button"
                            onClick={() => setMarkPaidModalOpen(true)}
                            className="aacore-btn2-success inline-flex items-center justify-center gap-2 w-full disabled:opacity-50"
                            disabled={saving || computedDue <= 0}
                            title={
                              computedDue <= 0
                                ? 'Invoice has no amount due to record'
                                : 'Record an offline payment and mark this invoice as paid'
                            }
                          >
                            <CheckCircle className="w-4 h-4 shrink-0" />
                            Mark as Paid
                          </button>
                        </div>
                        {showProcessPaymentAction ? (
                          <button
                            type="button"
                            onClick={() => void handleContinueToPayment()}
                            className="aacore-btn2-primary inline-flex items-center justify-center gap-2 w-full disabled:opacity-50 mt-3"
                            disabled={!canSave || saving}
                            title={
                              saving
                                ? 'Saving…'
                                : !canSave
                                  ? 'Include at least one line item and required fields'
                                  : 'Process payment for this invoice'
                            }
                          >
                            {saving ? (
                              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                            ) : (
                              <CreditCard className="w-4 h-4 shrink-0" />
                            )}
                            Process Payment
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSendInvoice}
                        className="aacore-btn2-primary inline-flex items-center justify-center gap-2 w-full disabled:opacity-50"
                        disabled={!canSendInvoice || saving}
                        title={
                          saving
                            ? 'Sending…'
                            : !customerEmail.trim()
                              ? 'Customer email is required to send'
                              : !canSendInvoice
                                ? 'Invoice must have at least one line item and required fields'
                                : 'Send invoice by email'
                        }
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                        ) : (
                          <Send className="w-4 h-4 shrink-0" />
                        )}
                        Send Invoice
                      </button>
                    )}
                    {!canSave && (
                      <div className="text-xs text-gray-500 mt-2">
                        Include at least one line item and fill in required fields to send the invoice or continue to payment.
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {openItemDropdownClientId &&
        itemDropdownAnchorRect &&
        !isReadOnly &&
        (() => {
          const li = lineItems.find((l) => l.clientId === openItemDropdownClientId);
          if (!li) return null;
          const q = (li.description || '').trim().toLowerCase();
          const filtered = q
            ? businessServices.filter((s) => s.name.toLowerCase().includes(q))
            : businessServices;
          return createPortal(
            <ul
              data-item-dropdown-list
              role="listbox"
              className="fixed z-[9999] max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1"
              style={{
                top: itemDropdownAnchorRect.top + itemDropdownAnchorRect.height + 4,
                left: itemDropdownAnchorRect.left,
                width: Math.max(itemDropdownAnchorRect.width, 200),
              }}
            >
              {filtered.map((s) => (
                <li
                  key={s.id}
                  role="option"
                  className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-gray-900"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    updateLine(li.clientId, {
                      description: s.name,
                      unitPriceCents: s.price != null ? moneyToCentsDigits(s.price) : li.unitPriceCents,
                    });
                    setOpenItemDropdownClientId(null);
                  }}
                >
                  <span className="font-medium">{s.name}</span>
                  {s.price != null && (
                    <span className="ml-2 text-gray-500">${Number(s.price).toFixed(2)}</span>
                  )}
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-gray-500 text-sm">No matching services</li>
              )}
            </ul>,
            document.body
          );
        })()}

      <MarkInvoicePaidModal
        isOpen={markPaidModalOpen}
        amountDue={computedDue}
        onClose={() => setMarkPaidModalOpen(false)}
        onSubmit={handleMarkInvoicePaidSubmit}
      />

      <CustomAlertModal
        isOpen={voidConfirmOpen}
        onClose={() => setVoidConfirmOpen(false)}
        title="Void this invoice?"
        message="This will void the invoice. A voided invoice cannot be sent or paid."
        type="error"
        showCancelButton
        confirmIcon={<Ban className="w-4 h-4" />}
        confirmText="Void Invoice"
        cancelText="Cancel"
        onConfirm={handleVoidInvoice}
      />
    </div>
  );
};

export default InvoiceDetailsPage;
